import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Participant } from '../types';

type ParticipantsCardProps = {
  participants: Participant[];
  currentUserRole: 'admin' | 'teacher' | 'student' | 'parent';
};

const ParticipantsCard: React.FC<ParticipantsCardProps> = ({ participants, currentUserRole }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const canModerate = currentUserRole === 'teacher' || currentUserRole === 'admin';

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return participants;
    return participants.filter((p) => p.name.toLowerCase().includes(q));
  }, [participants, searchQuery]);

  const teachers = filtered.filter((p) => p.role === 'teacher' || p.role === 'admin');
  const students = filtered.filter((p) => p.role === 'student');
  const parents = filtered.filter((p) => p.role === 'parent');

  const Row = ({ p, tone }: { p: Participant; tone: 'accent' | 'primary' | 'secondary' }) => (
    <div className="flex items-center justify-between p-2 rounded-md hover:bg-muted transition-colors duration-150">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
            tone === 'accent'
              ? 'bg-accent text-accent-foreground'
              : tone === 'secondary'
                ? 'bg-secondary text-secondary-foreground'
                : 'bg-primary text-primary-foreground'
          }`}
        >
          <span className="text-sm font-semibold">{p.name.charAt(0).toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            {p.isPresenting && <span className="text-xs text-error font-medium">Prezintă</span>}
            {p.isHandRaised && (
              <span className="text-xs text-warning font-medium flex items-center gap-1">
                <Icon name="Hand" size={12} className="text-warning" />
                Mână ridicată
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 ml-2">
        <Icon
          name={p.isAudioOn ? 'Mic' : 'MicOff'}
          size={16}
          className={p.isAudioOn ? 'text-success' : 'text-muted-foreground'}
        />
        <Icon
          name={p.isVideoOn ? 'Video' : 'VideoOff'}
          size={16}
          className={p.isVideoOn ? 'text-success' : 'text-muted-foreground'}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="Users" size={18} className="text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Participanți</h3>
          <span className="text-xs text-muted-foreground">({participants.length})</span>
        </div>
        {canModerate && (
          <span className="text-xs px-2 py-1 rounded border border-border bg-muted/30 text-muted-foreground">
            Moderator
          </span>
        )}
      </div>

      <div className="p-4 border-b border-border">
        <div className="relative">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Caută participanți..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div className="max-h-[45vh] overflow-y-auto">
        {teachers.length > 0 && (
          <div className="p-4 border-b border-border">
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-3">Profesori ({teachers.length})</div>
            <div className="space-y-1">
              {teachers.map((p) => (
                <Row key={p.id} p={p} tone="accent" />
              ))}
            </div>
          </div>
        )}

        {students.length > 0 && (
          <div className="p-4 border-b border-border">
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-3">Elevi ({students.length})</div>
            <div className="space-y-1">
              {students.map((p) => (
                <Row key={p.id} p={p} tone="primary" />
              ))}
            </div>
          </div>
        )}

        {parents.length > 0 && (
          <div className="p-4">
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-3">Părinți ({parents.length})</div>
            <div className="space-y-1">
              {parents.map((p) => (
                <Row key={p.id} p={p} tone="secondary" />
              ))}
            </div>
          </div>
        )}

        {teachers.length === 0 && students.length === 0 && parents.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">Nu există participanți.</div>
        )}
      </div>
    </div>
  );
};

export default ParticipantsCard;

